import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Brand } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth, homeFor } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const user = await login(form.email, form.password);
      toast.success(`Welcome back, ${user.name}`);
      navigate(homeFor(user));
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="navy-bg noise-overlay hidden lg:flex flex-col justify-between p-12 relative">
        <Link to="/"><Brand dark /></Link>
        <div className="relative z-10">
          <h1 className="text-4xl font-heading font-extrabold text-white leading-tight">
            Hire. Onboard. Manage.<br />Pay. Learn. Grow.
          </h1>
          <p className="text-white/60 mt-4 max-w-md">
            One platform for the complete workforce lifecycle — from job discovery to payroll and learning.
          </p>
        </div>
        <p className="text-white/40 text-sm relative z-10">Empowering Careers, Every Day.</p>
      </div>
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md" data-testid="login-page">
          <Link to="/" className="lg:hidden inline-block mb-10"><Brand /></Link>
          <h2 className="text-3xl font-heading font-bold text-[#0B1B3D]">Welcome back</h2>
          <p className="text-sm text-muted-foreground mt-2">Sign in to your Job Spring account.</p>
          <form onSubmit={submit} className="mt-8 space-y-5">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1.5"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                required
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="mt-1.5"
                data-testid="login-password-input"
              />
            </div>
            {error && (
              <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="login-error">
                {error}
              </p>
            )}
            <Button type="submit" disabled={busy} className="w-full rounded-full bg-[#0B1B3D] hover:bg-[#13264f]" data-testid="login-submit-btn">
              {busy ? "Signing in…" : "Sign In"}
            </Button>
          </form>
          <p className="text-sm text-muted-foreground mt-6 text-center">
            New to Job Spring?{" "}
            <Link to="/register" className="text-[#2563EB] font-semibold" data-testid="login-register-link">
              Create an account
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
